import React from 'react';
import i18n from 'i18n-js';
import { Text, View } from 'react-native';
import { NotProductiveBlockProps } from '@v2/pages/machines/common/types/props';
import {
  formatSinceTimeWithDayTranslationCase,
  ncFileStatuses,
  subStatuses,
  subStatusName,
} from '../../../../../../../../helpers';
import NcFileIcon from '@components/machines/components/ncFileIcon';
import { is24HourFormat, scale } from '@components/frame';
import { font } from '@styles/fonts';
import { useForceUpdateEverySecond } from '@v2/pages/common/hooks/useForceUpdateEverySecond';

interface Props extends NotProductiveBlockProps {}

const NotProductiveBlock = React.memo(
  ({ ncFileStatus, subStatus, ncFile, selected, lastModified, C, status }: Props) => {
    const color = C(selected ? 'Label/Light/AlwaysLight Secondary' : 'Label/Light/Tertiary');
    useForceUpdateEverySecond();

    const renderNcFileText = () => {
      if (ncFileStatus === ncFileStatuses.Unlinked) {
        return i18n.t('Production Information Missing');
      }

      return ncFile;
    };

    const _subStatus = subStatus as keyof typeof subStatusName;
    const sinceText = formatSinceTimeWithDayTranslationCase(lastModified, is24HourFormat);

    const renderInfo = () => {
      if (subStatus === subStatuses.MachineConnectorOff || subStatus === subStatuses.NotPingable || !ncFile) {
        return (
          <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', alignContent: 'center', marginTop: scale(1) }}>
              <View style={{ width: scale(10), height: scale(12), justifyContent: 'center' }}>
                <NcFileIcon {...{ status: ncFileStatus, machineStatus: status, subStatus, ncFile, C, selected }} />
              </View>
              <View style={{ marginLeft: scale(5.5), flex: 1 }}>
                <Text style={[{ color }, font['Subhead 1']]}>{i18n.t(subStatusName[subStatus as keyof typeof subStatusName] || subStatus)}</Text>
              </View>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', alignContent: 'center' }}>
              <View style={{ width: scale(10), height: scale(10) }} />
              <View style={{ marginLeft: scale(5.5), flex: 1 }}>
                <Text style={[{ color }, font['Subhead 1']]} numberOfLines={1}>
                  {sinceText}
                </Text>
              </View>
            </View>
          </View>
        );
      }

      if (ncFile) {
        return (
          <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', alignContent: 'center', marginTop: scale(1) }}>
              <View style={{ width: scale(10), height: scale(12), justifyContent: 'center' }}>
                <NcFileIcon {...{ status: ncFileStatus, ncFile, machineStatus: status, subStatus, C, selected }} />
              </View>
              <View style={{ marginLeft: scale(5.5), flex: 1 }}>
                <Text style={[{ color }, font['Subhead 1']]} numberOfLines={1} ellipsizeMode="middle">
                  {renderNcFileText()}
                </Text>
              </View>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', alignContent: 'center' }}>
              <View style={{ width: scale(10), height: scale(10) }} />
              <View style={{ marginLeft: scale(5.5), flex: 1 }}>
                <Text style={[{ color, fontVariant: ['tabular-nums'] }, font['Subhead 1']]} numberOfLines={1}>
                  {`${i18n.t(subStatusName[_subStatus] || subStatus)} ${sinceText}`}
                </Text>
              </View>
            </View>
          </View>
        );
      }
    };
    return <View style={{ flex: 1 }}>{renderInfo()}</View>;
  },
  (prev, next) =>
    prev.ncFileStatus === next.ncFileStatus &&
    prev.subStatus === next.subStatus &&
    prev.ncFile === next.ncFile &&
    prev.selected === next.selected &&
    prev.lastModified === next.lastModified &&
    prev.C.scheme === next.C.scheme
);

export { NotProductiveBlock };
